import * as React from "react";

import { Container, Grid, Stack } from "@mui/material";

import { Account, AccountNextStep } from "../../types/Account";
import { SectorIcon } from "../../components/atoms/SectorIcon";
import { CustomCollapsible } from "../../components/organisms/CustomCollapsible";
import { AccountUpdates } from "./AccountUpdates";
import { RequestDetails } from "./RequestDetails";
import { RequestOutcome } from "./RequestOutcome";
import { additionalServicesMap } from "@/types";
import { isQuilter } from "@/helpers/service-provider-helpers";
import { readableAccountType } from "@/utils/accounts";

export type AccountCollapsibleProps = {
  readonly caseId: string;
  readonly signature: string | null;
  readonly account: Account;
  readonly section: "blocked" | "processing" | "settled";
  readonly open: boolean;
};

export const AccountCollapsible: React.FC<AccountCollapsibleProps> = ({
  signature,
  caseId,
  account,
  section,
  open = false,
}) => {
  const [expanded, setExpanded] = React.useState(open);

  const handleToggle = React.useCallback(
    (active) => {
      setExpanded(active);
    },
    [setExpanded]
  );

  return (
    <AccountCollapsibleView
      caseId={caseId}
      signature={signature}
      account={account}
      expanded={expanded}
      section={section}
      handleToggle={handleToggle}
    />
  );
};

export type AccountCollapsibleViewProps = {
  readonly caseId: string;
  readonly signature: string | null;
  readonly account: Account;
  readonly expanded: boolean;
  readonly section: "blocked" | "processing" | "settled";
  readonly handleToggle: (active: boolean) => void;
};

export const AccountCollapsibleView: React.FC<AccountCollapsibleViewProps> = ({
  caseId,
  signature,
  account,
  expanded,
  handleToggle,
}) => {
  const accountHeading: string = account.additionalServicesDetails
    ? additionalServicesMap.get(account.additionalServicesDetails.type) ?? "Unknown Service"
    : account.serviceProvider?.companyName ?? "Unknown Company";
  return (
    <CustomCollapsible
      title={accountTitle(account, accountHeading)}
      onToggled={handleToggle}
      expanded={expanded}
    >
      <>
        <Container sx={{ padding: "15px 35px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack rowGap={2}>
                <RequestDetails account={account} />
                <RequestOutcome account={account} />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <AccountUpdates
                caseId={caseId}
                signature={signature}
                account={account}
                updateChat={expanded}
              />
            </Grid>
          </Grid>
        </Container>

        <br />
      </>
    </CustomCollapsible>
  );
};
function accountTitle(account: Account, accountHeading: string): React.ReactNode {
  if (isQuilter(account.serviceProvider?.id)) {
    accountHeading = accountHeading + " " + readableAccountType(account.accountType);
  }
  return (
    <>
      <span
        style={{
          fontSize: "1.4rem",
          verticalAlign: "middle",
          display: "inline-block",
          width: "40px",
        }}
      >
        {!account.additionalServicesAccount && account.serviceProvider && (
          <SectorIcon sector={account.serviceProvider.serviceProviderType} />
        )}
      </span>
      {accountHeading}
    </>
  );
}
